import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  // Chakra Popover is used here (not @kula-ai/components) because we need a
  // controlled, trigger-less announcement anchored to the nav item with a
  // one-time dismiss — kula-ai's Popover is hover/click-trigger oriented.
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  type PopoverContentProps,
} from '@chakra-ui/react';
import { Atom01, XClose } from '@kula-ai/icons-core';
import { NavLink } from './NavLink';
import { KText } from '@/ui-library/components';
import NavigationContext from '../context';

// One-time launch announcement for Ask Kula. Persisted via localStorage so it
// shows once and never again after the user dismisses it.
const VIEWED_KEY = 'viewed_ask_kula';

// Extracted into a typed constant (instead of inlining on <PopoverContent />)
// because the element carries enough token-typed style props that adding one
// more inline tips TS over its "union type too complex to represent" limit.
const announcementCardStyles: PopoverContentProps = {
  width: '400px',
  // `rootProps` targets the `.chakra-popover__popper` wrapper, whose stacking
  // context wins; a base z-index keeps the card above page content but below
  // modals/overlays (zIndex 'modal' = 1400).
  rootProps: { zIndex: 1 },
  bg: 'primary-25',
  border: '1px solid',
  borderColor: 'primary-400',
  borderRadius: '6px',
  boxShadow: '0px 12px 32px rgba(38, 34, 68, 0.12)',
  _focus: {
    outline: 'none',
    boxShadow: '0px 12px 32px rgba(38, 34, 68, 0.12)',
  },
  _focusVisible: { outline: 'none' },
  // Speech-bubble tail on the left edge pointing at the nav item — a rotated
  // square whose two outer edges carry the border while its body masks the
  // card's left border where the tail joins.
  _before: {
    content: '""',
    position: 'absolute',
    left: '-6px',
    top: '50%',
    width: '12px',
    height: '12px',
    bg: 'primary-25',
    borderLeft: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'primary-400',
    transform: 'translateY(-50%) rotate(45deg)',
  },
};

interface AskKulaNavLinkProps {
  icon: React.ElementType;
  link: string;
  title: string;
  testID?: string;
  isCollapsed?: boolean;
  // Lets callers fall back to a plain NavLink (no nudge) when not applicable.
  nudgeEnabled?: boolean;
}

export const AskKulaNavLink = ({
  nudgeEnabled = true,
  ...navLinkProps
}: AskKulaNavLinkProps) => {
  const [showNudge, setShowNudge] = useState(
    () => nudgeEnabled && localStorage.getItem(VIEWED_KEY) !== 'true'
  );
  const { isNavCollapsed } = useContext(NavigationContext) ?? {};
  const isFirstCollapseRun = useRef(true);

  useEffect(() => {
    if (nudgeEnabled && localStorage.getItem(VIEWED_KEY) === null) {
      localStorage.setItem(VIEWED_KEY, 'false');
    }
  }, [nudgeEnabled]);

  // The sidebar animates its width when collapsing (~0.25s), moving the anchor.
  // Chakra's Popper only repositions on scroll/resize, so pump resize events
  // across the transition window to keep the card glued to the nav item.
  useEffect(() => {
    if (!showNudge) return undefined;
    if (isFirstCollapseRun.current) {
      isFirstCollapseRun.current = false;
      return undefined;
    }
    let rafId = 0;
    let start: number | null = null;
    const DURATION = 320;
    const tick = (ts: number) => {
      if (start === null) start = ts;
      window.dispatchEvent(new Event('resize'));
      if (ts - start < DURATION) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isNavCollapsed, showNudge]);

  const dismissNudge = () => {
    localStorage.setItem(VIEWED_KEY, 'true');
    setShowNudge(false);
  };

  if (!nudgeEnabled) {
    return <NavLink {...navLinkProps} />;
  }

  return (
    <Popover
      isOpen={showNudge}
      placement="right"
      closeOnBlur={false}
      autoFocus={false}
      returnFocusOnClose={false}
      gutter={12}
      modifiers={[{ name: 'preventOverflow', options: { padding: 12 } }]}
    >
      <PopoverAnchor>
        <Box>
          <NavLink {...navLinkProps} />
        </Box>
      </PopoverAnchor>
      <PopoverContent {...announcementCardStyles}>
        <PopoverBody p="12px">
          <Flex justifyContent="space-between" alignItems="flex-start" mb="8px">
            <Flex alignItems="center" gap="8px">
              <Icon as={Atom01} boxSize="18px" color="secondary-700" />
              <KText
                fontSize="14px"
                fontWeight="medium"
                color="secondary-900"
                lineHeight="20px"
              >
                Meet Ask Kula
              </KText>
            </Flex>
            <Icon
              as={XClose}
              boxSize="16px"
              color="secondary-900"
              cursor="pointer"
              flexShrink={0}
              onClick={dismissNudge}
              data-test-id="ask-kula-nudge-close"
            />
          </Flex>
          <KText
            fontSize="13px"
            fontWeight="regular"
            color="secondary-700"
            lineHeight="20px"
          >
            Your AI assistant for recruiting. Search candidates, create jobs,
            run salary benchmarks and more - just by asking.
          </KText>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
